import {
  useId,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
  type ReactElement,
} from 'react'

/**
 * Small, dependency-free charts for Insights — PRD §9.3 accessibility: keyboard navigable, values
 * also printed as text (legend totals and a table twin), colour never the only cue.
 *
 * `ColumnChart` draws one to three series as grouped columns over a category axis (days or weeks).
 * Series take the chart colours in fixed order (`--op-color-chart-1`, `-2`), never by rank. The plot
 * is one tab stop: arrow keys move between columns and the readout announces the values.
 *
 * `ProportionBar` splits one whole into labelled parts (the outcome of every request in a period).
 * The bar is decoration; the list under it carries every count and share as text.
 */

export interface ColumnChartPoint {
  readonly key: string
  /** Short axis label ("Oct 3"). */
  readonly label: string
  /** Full label for the readout and the table ("Oct 3, 2026" or "Sep 28 – Oct 4"). */
  readonly title: string
  /** One value per series, in series order. */
  readonly values: readonly number[]
}

export interface ColumnChartProps {
  /** The chart's accessible name, usually the card title. */
  readonly label: string
  /** Series names, in colour order. */
  readonly series: readonly string[]
  readonly points: readonly ColumnChartPoint[]
  /** Header of the category column in the table view ("Day", "Week"). */
  readonly categoryLabel: string
  /** The disclosure that opens the table view. */
  readonly tableLabel: string
  readonly formatValue?: (value: number) => string
  readonly className?: string
  readonly 'data-testid'?: string
}

/** A 1–2–5 step that gives three or four gridlines up to at least `max`. */
export function niceScale(max: number): {
  readonly top: number
  readonly ticks: readonly number[]
} {
  if (!(max > 0)) return { top: 1, ticks: [0, 1] }
  const rough = max / 3
  const magnitude = 10 ** Math.floor(Math.log10(rough))
  const step = Math.max(1, ([1, 2, 5, 10].find((m) => m * magnitude >= rough) ?? 10) * magnitude)
  const top = Math.ceil(max / step) * step
  const ticks: number[] = []
  for (let tick = 0; tick <= top; tick += step) ticks.push(tick)
  return { top, ticks }
}

/** Which axis labels to print: every one when they fit, else the first, middle and last. */
export function axisLabelIndexes(count: number): readonly number[] {
  if (count <= 8) return Array.from({ length: count }, (_, index) => index)
  return [...new Set([0, Math.floor((count - 1) / 2), count - 1])]
}

const defaultFormat = (value: number): string => String(value)

export function ColumnChart({
  label,
  series,
  points,
  categoryLabel,
  tableLabel,
  formatValue = defaultFormat,
  className,
  'data-testid': testId,
}: ColumnChartProps): ReactElement {
  const id = useId()
  const [active, setActive] = useState<number | null>(null)
  const max = Math.max(0, ...points.flatMap((point) => point.values))
  const { top, ticks } = niceScale(max)
  const totals = series.map((_, index) =>
    points.reduce((sum, point) => sum + (point.values[index] ?? 0), 0),
  )
  const labelled = new Set(axisLabelIndexes(points.length))
  const current = active === null ? null : (points[active] ?? null)

  function move(event: KeyboardEvent<HTMLDivElement>): void {
    if (points.length === 0) return
    const last = points.length - 1
    const from = active ?? -1
    const next =
      event.key === 'ArrowRight'
        ? Math.min(last, from + 1)
        : event.key === 'ArrowLeft'
          ? Math.max(0, from === -1 ? last : from - 1)
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : event.key === 'Escape'
                ? null
                : undefined
    if (next === undefined) return
    event.preventDefault()
    setActive(next)
  }

  function track(event: PointerEvent<HTMLDivElement>): void {
    const box = event.currentTarget.getBoundingClientRect()
    if (box.width <= 0 || points.length === 0) return
    const ratio = (event.clientX - box.left) / box.width
    setActive(Math.min(points.length - 1, Math.max(0, Math.floor(ratio * points.length))))
  }

  // The readout sits over the active column, pinned inward near either edge.
  const edge =
    active === null || points.length < 3
      ? 'center'
      : active < points.length / 4
        ? 'start'
        : active >= (points.length * 3) / 4
          ? 'end'
          : 'center'

  return (
    <figure
      className={['op-chart', className].filter(Boolean).join(' ')}
      aria-labelledby={`${id}-label`}
      data-testid={testId}
    >
      <figcaption id={`${id}-label`} className="op-visually-hidden">
        {label}
      </figcaption>
      {series.length > 1 ? (
        <ul className="op-chart__legend">
          {series.map((name, index) => (
            <li key={name} data-testid={testId === undefined ? undefined : `${testId}-legend`}>
              <span className={`op-chart__key op-chart__key--${index + 1}`} aria-hidden={true} />
              <span>{name}</span>
              <strong className="op-chart__total">{formatValue(totals[index] ?? 0)}</strong>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="op-chart__frame">
        <div className="op-chart__ticks" aria-hidden={true}>
          {ticks.map((tick) => (
            <span
              key={tick}
              className="op-chart__tick"
              style={{ '--op-chart-at': tick / top } as CSSProperties}
            >
              {formatValue(tick)}
            </span>
          ))}
        </div>
        <div
          className="op-chart__plot"
          role="group"
          aria-roledescription="chart"
          aria-label={label}
          aria-describedby={`${id}-readout`}
          tabIndex={0}
          onKeyDown={move}
          onPointerMove={track}
          // A tap fires no move and its pointer "leaves" as the finger lifts: a touch picks the
          // column on press and keeps the readout until the next tap or blur.
          onPointerDown={track}
          onPointerLeave={(event) => {
            if (event.pointerType === 'mouse') setActive(null)
          }}
          onBlur={() => setActive(null)}
        >
          {ticks.map((tick) => (
            <span
              key={tick}
              className="op-chart__grid"
              aria-hidden={true}
              style={{ '--op-chart-at': tick / top } as CSSProperties}
            />
          ))}
          {points.map((point, index) => (
            <div
              key={point.key}
              className={['op-chart__group', index === active ? 'op-chart__group--active' : null]
                .filter(Boolean)
                .join(' ')}
              aria-hidden={true}
            >
              {series.map((name, slot) => {
                const value = point.values[slot] ?? 0
                return value <= 0 ? (
                  <span key={name} className="op-chart__bar op-chart__bar--empty" />
                ) : (
                  <span
                    key={name}
                    className={`op-chart__bar op-chart__bar--${slot + 1}`}
                    style={{ '--op-chart-at': value / top } as CSSProperties}
                  />
                )
              })}
              {index === active ? (
                <span className={`op-chart__readout op-chart__readout--${edge}`}>
                  <span className="op-chart__readout-title">{point.title}</span>
                  {series.map((name, slot) => (
                    <span key={name} className="op-chart__readout-row">
                      <span
                        className={`op-chart__line-key op-chart__key--${slot + 1}`}
                        aria-hidden={true}
                      />
                      <strong>{formatValue(point.values[slot] ?? 0)}</strong>
                      <span>{name}</span>
                    </span>
                  ))}
                </span>
              ) : null}
            </div>
          ))}
        </div>
        <div className="op-chart__axis" aria-hidden={true}>
          {/* Each label is centred under its column; the first and last are pinned to the plot's
              edges so they never run past the card. */}
          {points.map((point, index) =>
            labelled.has(index) ? (
              <span
                key={point.key}
                className={[
                  'op-chart__axis-label',
                  points.length > 1 && index === 0 ? 'op-chart__axis-label--start' : null,
                  points.length > 1 && index === points.length - 1
                    ? 'op-chart__axis-label--end'
                    : null,
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{ '--op-chart-x': (index + 0.5) / points.length } as CSSProperties}
              >
                {point.label}
              </span>
            ) : null,
          )}
        </div>
      </div>
      {/* Keyboard readers hear the active column; the table below is the full equivalent. */}
      <p id={`${id}-readout`} className="op-visually-hidden" aria-live="polite">
        {current === null
          ? ''
          : `${current.title}: ${series
              .map((name, slot) => `${name} ${formatValue(current.values[slot] ?? 0)}`)
              .join(', ')}`}
      </p>
      <details className="op-chart__table">
        <summary>{tableLabel}</summary>
        <div className="op-chart__table-scroll">
          <table>
            <caption className="op-visually-hidden">{label}</caption>
            <thead>
              <tr>
                <th scope="col">{categoryLabel}</th>
                {series.map((name) => (
                  <th key={name} scope="col">
                    {name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {points.map((point) => (
                <tr key={point.key}>
                  <th scope="row">{point.title}</th>
                  {series.map((name, slot) => (
                    <td key={name}>{formatValue(point.values[slot] ?? 0)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </figure>
  )
}

/* ProportionBar ------------------------------------------------------------ */

export type ProportionTone = 'success' | 'accent' | 'danger' | 'warning' | 'neutral'

export interface ProportionPart {
  readonly key: string
  readonly label: string
  readonly count: number
  readonly tone: ProportionTone
}

export interface ProportionBarProps {
  readonly label: string
  readonly parts: readonly ProportionPart[]
  /** "62%" — the share of the whole, formatted by the caller's locale. */
  readonly formatShare: (share: number) => string
  readonly className?: string
  readonly 'data-testid'?: string
}

export function ProportionBar({
  label,
  parts,
  formatShare,
  className,
  'data-testid': testId,
}: ProportionBarProps): ReactElement {
  const total = parts.reduce((sum, part) => sum + part.count, 0)
  return (
    <div className={['op-proportion', className].filter(Boolean).join(' ')} data-testid={testId}>
      {total === 0 ? null : (
        <div className="op-proportion__bar" aria-hidden={true}>
          {parts
            .filter((part) => part.count > 0)
            .map((part) => (
              <span
                key={part.key}
                className={`op-proportion__part op-proportion__part--${part.tone}`}
                style={{ flexGrow: part.count }}
              />
            ))}
        </div>
      )}
      <ul className="op-proportion__legend" aria-label={label}>
        {parts.map((part) => (
          <li
            key={part.key}
            data-testid={testId === undefined ? undefined : `${testId}-${part.key}`}
          >
            <span
              className={`op-proportion__key op-proportion__part--${part.tone}`}
              aria-hidden={true}
            />
            <span className="op-proportion__label">{part.label}</span>
            <strong className="op-proportion__count">{part.count}</strong>
            <span className="op-proportion__share">
              {total === 0 ? '' : formatShare(part.count / total)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
